import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import Tile from "../types/tile";
import TileType from "../types/tileType";
import Option from "../types/option";
import SearchResults from "../types/searchResults";
import SearchParams from "../types/searchParams";
import getSearchQuery from "../helpers/getSearchQuery";

interface TileState {
  status: "idle" | "loading" | "failed";
  tileSaveStatus: "idle" | "loading" | "failed";
  tileDetailStatus: "idle" | "loading" | "failed";
  tileTableStatus: "idle" | "loading" | "failed";
  tile: Tile;
  tableSearchParams: SearchParams;
  tileTable?: SearchResults;
  tileList?: Tile[];
  tileTypes?: TileType[];
  icons?: Option[];
  backgrounds?: Option[];
  breadcrumbs?: Option[];
  tileLoaded: { id: number; loaded: boolean; tableLoaded: boolean }[];
}

const initialState: TileState = {
  status: "idle",
  tileSaveStatus: "idle",
  tileDetailStatus: "idle",
  tileTableStatus: "idle",
  tile: {
    id: 0,
    title: "",
    backgroundImage: "",
    type: undefined,
  },
  tableSearchParams: {
    id: 0,
    query: "",
    pageNumber: 1,
    pageSize: 10,
  },
  tileList: [],
  tileLoaded: [],
};

export const createTile = createAsyncThunk<number, Tile>(
  "tile/create",
  async (tile: Tile) => {
    const response = await http.post<number>("/tile", tile, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

// TODO change the api to accept a number
export const duplicateTile = createAsyncThunk<number, number>(
  "tile/duplicate",
  async (id: number) => {
    const response = await http.post<number>("/tile/duplicate");
    return response.data;
  }
);

export const getTile = createAsyncThunk<
  Tile,
  { id: number; includeData: boolean; fullTile: boolean }
>("tile/fetch", async ({ id, includeData, fullTile }) => {
  const response = await http.get<Tile>(
    `/tile/${id}?includeData=${includeData}&fullTile=${fullTile}`
  );

  return response.data;
});

export const getTileDetail = createAsyncThunk<Tile, number>(
  "tile/fetch-detail",
  async (id: number) => {
    const response = await http.get<Tile>(
      `/tile/${id}?includeData=true&fullTile=false`
    );

    return response.data;
  }
);

export const getTable = createAsyncThunk<
  SearchResults,
  { search: SearchParams; export?: boolean }
>("tile/fetch-table", async ({ search }) => {
  const query = getSearchQuery(search);
  const response = await http.get<SearchResults>(`/tile?${query}`);
  return response.data;
});

export const deleteTile = createAsyncThunk<null, number>(
  "tile/delete",
  async (id: number) => {
    const response = await http.delete(`/tile/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const getTypes = createAsyncThunk<TileType[]>(
  "tile/get-types",
  async () => {
    const response = await http.get<TileType[]>(`/tile/types`);
    return response.data;
  }
);

export const getIcons = createAsyncThunk<Option[]>(
  "tile/get-icons",
  async () => {
    const response = await http.get<Option[]>(`/tile/icons`);
    return response.data;
  }
);

export const getBackgrounds = createAsyncThunk<Option[]>(
  "tile/get-backgrounds",
  async () => {
    const response = await http.get<Option[]>(`/tile/backgrounds`);
    return response.data;
  }
);

export const getBackgroundUrl = createAsyncThunk(
  "/tile/background-url",
  async (id: number) => {
    const response = await http.get(`/tile/background/${id}`);
    return response.data;
  }
);

export const getBreadcrumbs = createAsyncThunk<Option[], number>(
  "dashboard/fetch-breadcrumbs",
  async (id: number) => {
    const response = await http.get<Option[]>(`/tile/${id}/breadcrumbs`);
    return response.data;
  }
);

const tileSlice = createSlice({
  name: "tile",
  initialState,
  reducers: {
    selectTile(state, action) {
      state.tile = action.payload;
    },
    clearTileOptions(state) {
      state.backgrounds = initialState.backgrounds;
      state.icons = initialState.icons;
    },
    updateLoadingState(state, action) {
      const idx = state.tileLoaded.findIndex(
        (tile) => tile.id === action.payload.id
      );
      if (idx !== -1) {
        state.tileLoaded[idx].loaded = action.payload.loaded;
        state.tileLoaded[idx].tableLoaded = action.payload.tableLoaded;
      } else {
        state.tileLoaded.push({
          id: action.payload.id,
          loaded: action.payload.loaded,
          tableLoaded: action.payload.tableLoaded,
        });
      }
    },
    clearLoadingState(state, action) {
      state.tileLoaded = initialState.tileLoaded;
    },
    updateTableSearchParams(state, action) {
      state.tableSearchParams = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Post
      .addCase(createTile.pending, (state) => {
        state.tileSaveStatus = "loading";
      })
      .addCase(createTile.fulfilled, (state) => {
        state.tileSaveStatus = "idle";
      })
      .addCase(createTile.rejected, (state) => {
        state.tileSaveStatus = "failed";
      })
      // Duplicate
      .addCase(duplicateTile.pending, (state) => {
        state.status = "loading";
      })
      .addCase(duplicateTile.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(duplicateTile.rejected, (state) => {
        state.status = "failed";
      })
      // Get
      .addCase(getTable.pending, (state) => {
        state.tileTableStatus = "loading";
      })
      .addCase(getTable.fulfilled, (state, action) => {
        state.tileTableStatus = "idle";
        if (!action.meta.arg.export) {
          state.tileTable = action.payload;

          let tileLoaded = state.tileLoaded.find(
            (x) => x.id === action.payload.id
          );

          if (!tileLoaded || (tileLoaded && !tileLoaded.tableLoaded)) {
            tileSlice.caseReducers.updateLoadingState(state, {
              payload: {
                id: action.payload.id!,
                loaded: tileLoaded?.loaded,
                tableLoaded: true,
              },
              type: action.type,
            });
          }
        }
      })
      .addCase(getTable.rejected, (state, action) => {
        state.tileTableStatus = "failed";
      })
      .addCase(getTile.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getTile.fulfilled, (state, action) => {
        state.status = "idle";

        let tileLoaded = state.tileLoaded.find(
          (x) => x.id === action.payload.id
        );

        if (!tileLoaded || (tileLoaded && !tileLoaded.loaded)) {
          tileSlice.caseReducers.updateLoadingState(state, {
            payload: {
              id: action.payload.id!,
              loaded: true,
              tableLoaded: tileLoaded?.tableLoaded,
            },
            type: action.type,
          });
        }
      })
      .addCase(getTile.rejected, (state, action) => {
        state.status = "failed";
      })

      .addCase(getTileDetail.pending, (state) => {
        state.tileDetailStatus = "loading";
      })
      .addCase(getTileDetail.fulfilled, (state, action) => {
        state.tileDetailStatus = "idle";
        state.tile = action.payload;

        let tileLoaded = state.tileLoaded.find(
          (x) => x.id === action.payload.id
        );

        if (!tileLoaded || (tileLoaded && !tileLoaded.loaded)) {
          tileSlice.caseReducers.updateLoadingState(state, {
            payload: {
              id: action.payload.id!,
              loaded: true,
              tableLoaded: tileLoaded?.tableLoaded,
            },
            type: action.type,
          });
        }
      })
      .addCase(getTileDetail.rejected, (state, action) => {
        state.tileDetailStatus = "failed";
      })
      // Delete
      .addCase(deleteTile.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteTile.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteTile.rejected, (state) => {
        state.status = "failed";
      })
      // Get Types
      .addCase(getTypes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getTypes.fulfilled, (state, action) => {
        state.status = "idle";
        state.tileTypes = action.payload;
      })
      .addCase(getTypes.rejected, (state) => {
        state.status = "failed";
      })
      // Get Icons
      .addCase(getIcons.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getIcons.fulfilled, (state, action) => {
        state.status = "idle";
        state.icons = action.payload;
      })
      .addCase(getIcons.rejected, (state) => {
        state.status = "failed";
      })
      // Get Backgrounds
      .addCase(getBackgrounds.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getBackgrounds.fulfilled, (state, action) => {
        state.status = "idle";
        state.backgrounds = action.payload;
      })
      .addCase(getBackgrounds.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getBreadcrumbs.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getBreadcrumbs.fulfilled, (state, action) => {
        state.status = "idle";
        state.breadcrumbs = action.payload;
      })
      .addCase(getBreadcrumbs.rejected, (state, action) => {
        state.status = "failed";
        state.breadcrumbs = undefined;
      });
  },
});

export const {
  selectTile,
  clearTileOptions,
  updateLoadingState,
  clearLoadingState,
  updateTableSearchParams,
} = tileSlice.actions;

export default tileSlice.reducer;
